// This week: seven days from today. The type filters in the header are the legend (colour and count) and are
// remembered; at most three items a day, then "+N more" opens the day in the drawer.
import { useState } from 'react';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { Link } from 'react-router-dom';
import type { ActivityType, TodayData, TodayItem } from '@dpw/shared';
import { Progress } from '../../ui/bits';
import { listItem, spring } from '../../lib/motion';
import { TYPE_CLS, TYPE_COLOR, dayNum, isMeeting, monthOf, progressColor, weekdayOf } from './util';
import type { DrawerState } from './Drawer';

type Filter = ActivityType | 'milestone';
const FILTERS: [Filter, string, string][] = [
  ['meeting', 'Meetings', TYPE_COLOR.meeting], ['workshop', 'Workshops', TYPE_COLOR.workshop], ['deliverable', 'Deliverables', TYPE_COLOR.deliverable],
  ['task', 'Tasks', TYPE_COLOR.task], ['milestone', 'Milestones', 'var(--ink)'],
];
const KEY = 'dpw.today.hidden';
const MAX = 3;

function load(): Filter[] {
  try { const v = JSON.parse(localStorage.getItem(KEY) ?? '[]') as unknown; return Array.isArray(v) ? v.filter((x): x is Filter => FILTERS.some(f => f[0] === x)) : []; } catch { return []; }
}

export function Week({ week, today, phone, onOpen }: { week: TodayData['week']; today: string; phone: boolean; onOpen: (s: DrawerState) => void }) {
  const [hidden, setHidden] = useState<Filter[]>(load);
  const save = (h: Filter[]) => { setHidden(h); try { localStorage.setItem(KEY, JSON.stringify(h)); } catch { /* not remembered */ } };
  const shown = (x: TodayItem) => !hidden.includes(x.type) && !(x.isMilestone && hidden.includes('milestone'));
  const all = week.flatMap(d => d.items);
  const count = (f: Filter) => (f === 'milestone' ? all.filter(x => x.isMilestone).length : all.filter(x => x.type === f).length);
  const cols = week.map(d => (d.weekend ? '.7fr' : '1fr')).join(' ');
  return (
    <section className="pnl week" aria-label="This week">
      <div className="sech"><b>This week</b>
        <div className="wfl" role="group" aria-label="Show or hide by type">
          {FILTERS.map(([f, label, c]) => {
            const off = hidden.includes(f);
            return (
              <button key={f} type="button" className={`wf${f === 'milestone' ? ' ms' : ''}${off ? ' off' : ''}`} style={{ ['--c' as string]: c }} aria-pressed={!off}
                onClick={() => save(off ? hidden.filter(x => x !== f) : [...hidden, f])}><i /><span>{label}</span><em>{count(f)}</em></button>
            );
          })}
          {hidden.length > 0 && <button type="button" className="wfall" onClick={() => save([])}>Show all</button>}
        </div>
        <Link className="bt ghost" to="/calendar">Open calendar</Link></div>
      <div className="wk" style={phone ? undefined : { gridTemplateColumns: cols }}>
        {week.map(d => {
          const items = d.items.filter(shown);
          const more = items.length - MAX;
          return (
            <div key={d.date} className={`wd${d.weekend ? ' we' : ''}${d.date === today ? ' now' : ''}`} aria-label={`${weekdayOf(d.date)} ${dayNum(d.date)} ${monthOf(d.date)}`}>
              <div className="dh"><b>{weekdayOf(d.date)} {dayNum(d.date)}</b><span>{d.date === today ? 'Today' : d.weekend ? 'Weekend' : monthOf(d.date)}</span></div>
              <LayoutGroup id={`wd-${d.date}`}>
                <AnimatePresence initial={false} mode="popLayout">
                  {items.slice(0, MAX).map(x => (
                    <motion.button key={x.id} type="button" layout variants={listItem} initial="initial" animate="animate" exit="exit" transition={spring.gentle}
                      className={`wi ${TYPE_CLS[x.type]}`} onClick={() => onOpen({ kind: 'item', id: x.id })}>
                      <span className="wt">{x.start ?? (isMeeting(x.type) ? 'All day' : 'Due')}</span>
                      <b>{x.isMilestone ? '◆ ' : ''}{x.title}</b>
                      <span className="ww">{x.trackName} · {x.waitingOn ? `waiting on ${x.waitingOn}` : x.owner?.split(' ')[0] ?? 'No owner'}</span>
                      {(isMeeting(x.type) ? x.needsPrep && x.stepsTotal > 0 : x.progress > 0 || x.stepsTotal > 0) && (
                        <span className="wp"><Progress value={isMeeting(x.type) ? Math.round(100 * x.stepsDone / x.stepsTotal) : x.progress} color={progressColor(x)} />
                          <em>{isMeeting(x.type) ? `${Math.round(100 * x.stepsDone / x.stepsTotal)}% prep` : `${x.progress}% done`}</em></span>
                      )}
                    </motion.button>
                  ))}
                </AnimatePresence>
              </LayoutGroup>
              {more > 0 && <button type="button" className="wmore" onClick={() => onOpen({ kind: 'day', date: d.date })}>+{more} more</button>}
              {!items.length && <span className="wem">{d.items.length ? 'Hidden by filters' : d.weekend ? 'Nothing planned' : 'Free'}</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
